import React from 'react';
import { Box, Grid2, Link, Typography, Container, Paper, Button, useTheme, Icon } from '@mui/material';
import { useNavigate } from 'react-router-dom';

const EliminacionDatos = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const handleVolverInicio = () => {
    navigate('/');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const sections = [
    {
      id: "como-solicitar",
      title: "1. Cómo solicitar la eliminación de tus datos",
      icon: "delete_forever",
      content: (
        <>
          <Typography sx={{ mb: 2, color: theme.palette.text.secondary, lineHeight: 1.7 }}>
            Si utilizas <strong>Mi Rifa App</strong> y deseas que eliminemos los datos personales asociados a tu cuenta,
            puedes solicitarlo enviándonos un correo electrónico. Atenderemos tu petición conforme a las normativas
            aplicables sobre protección de datos.
          </Typography>
          <Typography sx={{ mb: 2, color: theme.palette.text.secondary, lineHeight: 1.7 }}>
            Para iniciar el proceso, escríbenos a la siguiente dirección de correo:
          </Typography>
          <Box
            sx={{
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : '#f8fafc',
              borderRadius: '10px',
              p: 2.5,
              border: theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid #e2e8f0',
              textAlign: 'center',
            }}
          >
            <Typography sx={{ mb: 0.5, color: theme.palette.text.secondary, fontSize: '0.9rem' }}>
              Correo para solicitudes de eliminación de datos:
            </Typography>
            <Link
              href="mailto:administration@rauhtech.com?subject=Solicitud%20de%20eliminaci%C3%B3n%20de%20datos%20-%20Mi%20Rifa%20App"
              sx={{
                color: theme.palette.mode === 'dark' ? '#818cf8' : theme.palette.primary.main,
                fontWeight: 800,
                fontSize: '1.15rem',
                textDecoration: 'none',
              }}
            >
              administration@rauhtech.com
            </Link>
          </Box>
        </>
      )
    },
    {
      id: "que-incluir",
      title: "2. Qué información debes incluir en tu solicitud",
      icon: "edit_note",
      content: (
        <>
          <Typography sx={{ mb: 2, color: theme.palette.text.secondary, lineHeight: 1.7 }}>
            Para poder identificar tu cuenta y procesar tu solicitud de forma segura, te pedimos que incluyas la
            siguiente información en tu correo:
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
            {[
              "El asunto: \"Solicitud de eliminación de datos - Mi Rifa App\".",
              "El nombre y apellido con el que te registraste en la aplicación.",
              "El correo electrónico y/o número de teléfono asociado a tu cuenta.",
              "Una breve indicación de que deseas eliminar tus datos personales."
            ].map((text, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1, bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : '#f8fafc', borderRadius: '8px', border: theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid #e2e8f0' }}>
                <Box sx={{ width: 22, height: 22, borderRadius: '6px', bgcolor: theme.palette.mode === 'dark' ? 'rgba(129, 140, 248, 0.15)' : 'rgba(45, 52, 132, 0.08)', color: theme.palette.mode === 'dark' ? '#818cf8' : theme.palette.primary.main, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 900, flexShrink: 0 }}>
                  {i + 1}
                </Box>
                <Typography variant="body2" sx={{ color: theme.palette.text.primary, fontWeight: 600 }}>
                  {text}
                </Typography>
              </Box>
            ))}
          </Box>
        </>
      )
    },
    {
      id: "que-eliminamos",
      title: "3. Qué datos se eliminan",
      icon: "cleaning_services",
      content: (
        <>
          <Typography sx={{ mb: 2.5, color: theme.palette.text.secondary, lineHeight: 1.7 }}>
            Al procesar tu solicitud, eliminaremos los datos personales que mantenemos asociados a tu cuenta, entre ellos:
          </Typography>
          <Grid2 container spacing={2}>
            {[
              { title: "Datos de Identidad", desc: "Nombre y apellido registrados en la aplicación." },
              { title: "Datos de Contacto", desc: "Correo electrónico y número de teléfono móvil." },
              { title: "Información de tu Cuenta", desc: "Datos de perfil y la información generada durante el uso de la app." }
            ].map((item, index) => (
              <Grid2 size={{ xs: 12, md: 4 }} key={index}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: '10px',
                    border: theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid #e2e8f0',
                    bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : '#f8fafc',
                    height: '100%'
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: theme.palette.mode === 'dark' ? '#818cf8' : theme.palette.primary.main, mb: 0.8 }}>
                    {item.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.6 }}>
                    {item.desc}
                  </Typography>
                </Box>
              </Grid2>
            ))}
          </Grid2>
          <Box
            sx={{
              mt: 2.5,
              p: 2,
              borderRadius: '10px',
              bgcolor: 'rgba(241, 91, 40, 0.08)',
              border: '1px solid rgba(241, 91, 40, 0.25)',
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 700, color: theme.palette.secondary.main }}>
              Nota importante:
            </Typography>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 0.5, lineHeight: 1.6 }}>
              Es posible que debamos conservar cierta información durante un tiempo limitado cuando exista una
              obligación legal que así lo exija (por ejemplo, registros necesarios para el cumplimiento normativo).
              En esos casos, los datos se conservarán únicamente por el período requerido y luego serán eliminados.
            </Typography>
          </Box>
        </>
      )
    },
    {
      id: "plazos",
      title: "4. Plazos de respuesta",
      icon: "schedule",
      content: (
        <Box sx={{ p: 2.5, bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : '#f8fafc', borderRadius: '10px', border: theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid #e2e8f0' }}>
          <Typography sx={{ mb: 2, color: theme.palette.text.secondary, lineHeight: 1.7 }}>
            Confirmaremos la recepción de tu solicitud y procederemos a la eliminación de tus datos dentro de un plazo
            razonable, que normalmente no excederá los <strong>30 días</strong> desde que verifiquemos tu identidad.
          </Typography>
          <Typography sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
            Si necesitamos información adicional para localizar tu cuenta o completar el proceso, nos pondremos en
            contacto contigo a través del correo electrónico desde el que realizaste la solicitud.
          </Typography>
        </Box>
      )
    },
    {
      id: "contacto",
      title: "5. Contacto",
      icon: "support_agent",
      content: (
        <Box sx={{ p: 2.5, bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : '#f8fafc', borderRadius: '10px', border: theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid #e2e8f0' }}>
          <Typography sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
            Si tienes cualquier duda sobre este proceso o sobre el tratamiento de tus datos personales, puedes
            escribirnos en cualquier momento a{' '}
            <Link href="mailto:administration@rauhtech.com" sx={{ color: theme.palette.mode === 'dark' ? '#818cf8' : theme.palette.primary.main, fontWeight: 700, textDecoration: 'none' }}>
              administration@rauhtech.com
            </Link>
            . Estaremos encantados de ayudarte.
          </Typography>
        </Box>
      )
    }
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: theme.palette.background.default, color: theme.palette.text.primary }}>
      {/* Hero Header Corporativo */}
      <Box
        sx={{
          background:
            theme.palette.mode === 'dark'
              ? 'linear-gradient(180deg, #1e293b 0%, #0f172a 100%)'
              : 'linear-gradient(145deg, #1c215e 0%, #2d3484 100%)',
          color: 'white',
          pt: { xs: 12, md: 15 },
          pb: { xs: 6, md: 8 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            width: 220,
            height: 220,
            borderRadius: '50%',
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            top: -50,
            right: 40,
            pointerEvents: 'none',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            width: 120,
            height: 120,
            borderRadius: '50%',
            backgroundColor: 'rgba(255, 255, 255, 0.04)',
            bottom: -40,
            left: '20%',
            pointerEvents: 'none',
          }}
        />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Button
            onClick={handleVolverInicio}
            startIcon={<Icon>arrow_back</Icon>}
            sx={{
              color: 'white',
              fontWeight: 700,
              bgcolor: 'rgba(255, 255, 255, 0.12)',
              borderRadius: '8px',
              mb: 3,
              '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.22)' },
            }}
          >
            Volver al Inicio
          </Button>

          <Typography
            variant="h3"
            component="h1"
            sx={{
              fontWeight: 900,
              fontSize: { xs: '2rem', md: '2.8rem' },
              mb: 1,
              letterSpacing: '-0.02em',
            }}
          >
            Eliminación de Datos
          </Typography>

          <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '1.05rem', mb: 2 }}>
            Mi Rifa App • RAUH TECH LTDA
          </Typography>

          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.5, bgcolor: 'rgba(255, 255, 255, 0.1)', borderRadius: '6px' }}>
            <Icon sx={{ fontSize: 16, color: '#10b981' }}>schedule</Icon>
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'rgba(255, 255, 255, 0.9)' }}>
              Última actualización: 6 de septiembre de 2026
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Contenido Principal */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        {/* Tarjeta de Introducción */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 4 },
            mb: 4,
            borderRadius: '12px',
            bgcolor: theme.palette.background.paper,
            border: theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid #e2e8f0',
            boxShadow: theme.palette.mode === 'dark' ? '0 4px 20px rgba(0, 0, 0, 0.4)' : '0 2px 10px rgba(0, 0, 0, 0.03)',
          }}
        >
          <Typography sx={{ color: theme.palette.text.secondary, lineHeight: 1.8, fontSize: '1rem' }}>
            En <strong>RAUH TECH LTDA</strong> respetamos tu derecho a controlar tu información personal. Si usas{' '}
            <strong>Mi Rifa App</strong> y deseas eliminar los datos que hemos recopilado sobre ti, en esta página te
            explicamos de forma sencilla cómo solicitarlo, qué información debes proporcionarnos y qué sucede con tus
            datos una vez recibimos tu petición.
          </Typography>
        </Paper>

        {/* Secciones */}
        {sections.map((section) => (
          <Paper
            key={section.id}
            elevation={0}
            sx={{
              p: { xs: 3, md: 4 },
              mb: 3.5,
              borderRadius: '12px',
              bgcolor: theme.palette.background.paper,
              border: theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid #e2e8f0',
              boxShadow: theme.palette.mode === 'dark' ? '0 4px 20px rgba(0, 0, 0, 0.4)' : '0 2px 10px rgba(0, 0, 0, 0.03)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              {/* Outlined Icon Box */}
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: '10px',
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.04)' : '#f8fafc',
                  border: theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid #e2e8f0',
                  color: theme.palette.primary.main,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Icon sx={{ fontSize: 24, color: theme.palette.mode === 'dark' ? '#818cf8' : theme.palette.primary.main }}>{section.icon}</Icon>
              </Box>
              <Typography
                variant="h6"
                component="h2"
                sx={{
                  fontWeight: 800,
                  color: theme.palette.text.primary,
                  fontSize: { xs: '1.15rem', md: '1.3rem' },
                }}
              >
                {section.title}
              </Typography>
            </Box>

            {section.content}
          </Paper>
        ))}
      </Container>
    </Box>
  );
};

export default EliminacionDatos;
